import { withGuestQuestionClaimLock } from './guestQuestionClaimLock';
import type { Language } from '../contexts/TranslationContext';

const DRAFT_KEY = 'palen.guest-question';
const CLAIM_KEY = 'palen.guest-question.claim';
const CHANGE_EVENT = 'palen-guest-question-changed';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;
export const GUEST_QUESTION_MAX_LENGTH = 3_000;

export type LandingQuestionOrigin = 'typed' | 'example';

export interface GuestQuestion {
  id: string;
  question: string;
  language: Language;
  createdAt: number;
  autoSubmit?: boolean;
  origin?: LandingQuestionOrigin;
  recoveryToken?: string;
  recoveryUserId?: string;
}

export interface GuestQuestionClaim {
  id: string;
  userId: string;
  createdAt: number;
  status: 'running' | 'complete' | 'failed';
  conversationId: string | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isFresh(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
    && value <= Date.now() && Date.now() - value < MAX_AGE_MS;
}

function readRecord(key: string): Record<string, unknown> | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const value: unknown = JSON.parse(raw);
    if (isRecord(value) && isFresh(value.createdAt)) return value;
    window.localStorage.removeItem(key);
  } catch {
    // Storage restrictions must never turn a browser draft into a network call.
  }
  return null;
}

type RecoveryOverride = {
  id: string; token: string; userId: string; version: number;
  status: 'bound' | 'manual' | 'discarded';
};

export class GuestQuestionService {
  private ownedDraftId: string | null = null;
  private resetVersion = 0;
  private authenticatedUserId: string | null = null;
  private canAutoSubmitLandingExample = false;
  private recoveryOverride: RecoveryOverride | null = null;

  getResetVersion(): number { return this.resetVersion; }

  cancelPendingRequests(): void {
    this.resetVersion += 1;
    const stored = readRecord(DRAFT_KEY);
    this.recoveryOverride = typeof stored?.id === 'string' && typeof stored.recoveryToken === 'string'
      ? { id: stored.id, token: stored.recoveryToken, userId: '', version: this.resetVersion, status: 'discarded' } : null;
  }

  getHandoffMarker(): string {
    return `${this.read()?.id ?? ''}:${readRecord(CLAIM_KEY)?.id ?? ''}`;
  }

  setAuthenticatedUserId(userId: string | null, canAutoSubmitLandingExample = false): void {
    const canAutoSubmit = Boolean(userId) && canAutoSubmitLandingExample === true;
    if (this.authenticatedUserId === userId && this.canAutoSubmitLandingExample === canAutoSubmit) return;
    this.authenticatedUserId = userId;
    this.canAutoSubmitLandingExample = canAutoSubmit;
    if (typeof window !== 'undefined') window.dispatchEvent(new Event(CHANGE_EVENT));
  }

  isAuthenticatedUser(userId: string): boolean {
    return Boolean(userId) && this.authenticatedUserId === userId;
  }

  save(question: string, language: Language, options: { autoSubmit?: boolean; origin?: LandingQuestionOrigin } = {}): boolean {
    const normalized = question.trim();
    if (!normalized || normalized.length > GUEST_QUESTION_MAX_LENGTH
      || (language !== 'fr' && language !== 'en')
      || (options.origin !== undefined && options.origin !== 'typed' && options.origin !== 'example')) return false;
    const draft: GuestQuestion = {
      id: crypto.randomUUID(), question: normalized, language, createdAt: Date.now(), origin: options.origin ?? 'typed',
      ...(options.autoSubmit === false ? { autoSubmit: false } : {}),
    };
    try {
      window.localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
      window.localStorage.removeItem(CLAIM_KEY);
      this.ownedDraftId = draft.id;
      this.recoveryOverride = null;
      window.dispatchEvent(new Event(CHANGE_EVENT));
      return true;
    } catch {
      return false;
    }
  }

  read(): GuestQuestion | null {
    const value = readRecord(DRAFT_KEY);
    if (!value || typeof value.id !== 'string' || !value.id
      || typeof value.question !== 'string' || !value.question.trim()
      || value.question.length > GUEST_QUESTION_MAX_LENGTH
      || (value.language !== 'fr' && value.language !== 'en')
      || (value.autoSubmit !== undefined && typeof value.autoSubmit !== 'boolean')
      || (value.origin !== undefined && value.origin !== 'typed' && value.origin !== 'example')
      || (value.recoveryToken !== undefined && (typeof value.recoveryToken !== 'string' || !/^[A-Za-z0-9_-]{43}$/.test(value.recoveryToken)))
      || (value.recoveryUserId !== undefined && (typeof value.recoveryUserId !== 'string' || !value.recoveryUserId))) return null;
    const override = this.recoveryOverride;
    const applies = override?.version === this.resetVersion && override.id === value.id && override.token === value.recoveryToken;
    if (applies && override.status === 'discarded') return null;
    return {
      id: value.id, question: value.question, language: value.language,
      ...(value.origin === undefined ? {} : { origin: value.origin }),
      createdAt: value.createdAt as number, ...(value.autoSubmit === false ? { autoSubmit: false } : {}),
      ...(typeof value.recoveryToken === 'string' && !(applies && override.status === 'manual') ? { recoveryToken: value.recoveryToken } : {}),
      ...(applies ? { recoveryUserId: override.userId, autoSubmit: false }
        : typeof value.recoveryUserId === 'string' ? { recoveryUserId: value.recoveryUserId } : {}),
    };
  }

  readForUser(userId: string): GuestQuestion | null {
    const draft = this.read();
    return draft && (!draft.recoveryUserId || draft.recoveryUserId === userId) ? draft : null;
  }

  attachRecovery(id: string, recoveryToken: string): boolean {
    const draft = this.read();
    if (!draft || draft.id !== id || !/^[A-Za-z0-9_-]{43}$/.test(recoveryToken)) return false;
    return this.writeRecovery({ ...draft, autoSubmit: false, recoveryToken });
  }

  bindRecovery(id: string, userId: string): GuestQuestion | null {
    const draft = this.readForUser(userId);
    if (!userId || draft?.id !== id || !draft.recoveryToken) return null;
    if (draft.recoveryUserId === userId) return draft;
    const bound = { ...draft, recoveryUserId: userId };
    if (!this.writeRecovery(bound)) {
      this.recoveryOverride = { id, token: draft.recoveryToken, userId, version: this.resetVersion, status: 'bound' };
      window.dispatchEvent(new Event(CHANGE_EVENT));
    }
    return bound;
  }

  allowRecoveryRetry(id: string, userId: string): void {
    const draft = this.readForUser(userId);
    if (!userId || draft?.id !== id || !draft.recoveryToken) return;
    const { recoveryToken, ...manual } = draft;
    if (!this.writeRecovery({ ...manual, recoveryUserId: userId, autoSubmit: false })) {
      // Only a server-confirmed terminal operation may become a manual retry.
      // Keep the original stored proof fenced if writes stop working mid-flow.
      this.recoveryOverride = { id, token: recoveryToken, userId, version: this.resetVersion, status: 'manual' };
      window.dispatchEvent(new Event(CHANGE_EVENT));
    }
  }

  private writeRecovery(draft: GuestQuestion): boolean {
    try {
      window.localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
      window.dispatchEvent(new Event(CHANGE_EVENT));
      return true;
    } catch { return false; }
  }

  readClaim(userId: string): GuestQuestionClaim | null {
    const value = readRecord(CLAIM_KEY);
    if (!value || value.userId !== userId || typeof value.id !== 'string'
      || !['running', 'complete', 'failed'].includes(String(value.status))
      || (value.conversationId !== null && typeof value.conversationId !== 'string')) return null;
    return value as unknown as GuestQuestionClaim;
  }

  hasIntent(userId: string): boolean {
    return Boolean(this.readForUser(userId) || this.readClaim(userId));
  }

  async claim(userId: string): Promise<GuestQuestion | null> {
    const pending = this.readForUser(userId);
    if (!pending || !this.canClaimAutomatically(userId, pending)) return null;
    const hasWebLocks = typeof navigator.locks?.request === 'function';
    if (hasWebLocks) {
      try {
        return await navigator.locks.request(DRAFT_KEY, () => this.take(userId));
      } catch {
        // A restricted Web Locks API can still use database coordination.
      }
    }
    try {
      return await withGuestQuestionClaimLock(() => this.take(userId));
    } catch {
      // Keep an unclaimed question editable if database coordination is restricted.
    }
    // Never bypass a lock that another tab might still acquire successfully.
    // A restricted API leaves the question available for an explicit send.
    if (hasWebLocks || typeof indexedDB !== 'undefined') return null;
    // Without cross-tab locking only the runtime that saved the draft may send.
    // A verification tab can follow its conversation without creating a second one.
    return this.read()?.id === this.ownedDraftId ? this.take(userId) : null;
  }

  private canClaimAutomatically(userId: string, draft: GuestQuestion): boolean {
    return this.isAuthenticatedUser(userId) && draft.autoSubmit !== false && !draft.recoveryToken
      && (draft.origin === 'typed' || this.canAutoSubmitLandingExample);
  }

  private take(userId: string): GuestQuestion | null {
    const draft = this.readForUser(userId);
    if (!draft || !this.canClaimAutomatically(userId, draft)) return null;
    if (readRecord(CLAIM_KEY)?.id === draft.id) return null;
    const claim: GuestQuestionClaim = {
      id: draft.id, userId, createdAt: Date.now(),
      status: 'running', conversationId: null,
    };
    try {
      window.localStorage.setItem(CLAIM_KEY, JSON.stringify(claim));
      window.localStorage.removeItem(DRAFT_KEY);
      this.ownedDraftId = null;
      window.dispatchEvent(new Event(CHANGE_EVENT));
      return draft;
    } catch {
      return null;
    }
  }

  discardPending(id: string): void {
    if (this.read()?.id !== id) return;
    const stored = readRecord(DRAFT_KEY);
    if (stored?.id === id && typeof stored.recoveryToken === 'string') {
      this.recoveryOverride = { id, token: stored.recoveryToken, userId: '', version: this.resetVersion, status: 'discarded' };
    }
    try { window.localStorage.removeItem(DRAFT_KEY); } catch { /* An in-memory fence still retires this operation. */ }
    this.ownedDraftId = null;
    window.dispatchEvent(new Event(CHANGE_EVENT));
  }

  updateClaim(userId: string, id: string, update: Partial<Pick<GuestQuestionClaim, 'status' | 'conversationId'>>): void {
    const claim = this.readClaim(userId);
    if (!claim || claim.id !== id) return;
    try {
      window.localStorage.setItem(CLAIM_KEY, JSON.stringify({ ...claim, ...update }));
      window.dispatchEvent(new Event(CHANGE_EVENT));
    } catch {
      // The owning chat still receives the response if storage becomes unavailable.
    }
  }

  subscribe(onChange: () => void): () => void {
    const onStorage = (event: StorageEvent) => {
      if (event.key === CLAIM_KEY || event.key === DRAFT_KEY || event.key === null) onChange();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener(CHANGE_EVENT, onChange);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(CHANGE_EVENT, onChange);
    };
  }

  clear(): void {
    this.cancelPendingRequests();
    this.ownedDraftId = null;
    this.authenticatedUserId = null;
    this.canAutoSubmitLandingExample = false;
    try {
      window.localStorage.removeItem(DRAFT_KEY);
      window.localStorage.removeItem(CLAIM_KEY);
    } catch {
      // Logout must still complete when storage is disabled.
    }
    window.dispatchEvent(new Event(CHANGE_EVENT));
  }
}

export const guestQuestionService = new GuestQuestionService();
